import { TeamMember } from '../services/team.service';
import { useTeamMemberships } from '../hooks/useTeamMemberships';
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useEvent } from './EventContext';
import { genericEventService } from '../services/firebase/generic-event.service';
import { findClosestEventByDate, shouldAutoSelectClosestEvent, markManualEventSelection, clearManualEventSelection } from '../utils/event.utils';

import { UnifiedEvent } from '../types/common';

// Interface pour le contexte unifié
interface UnifiedEventContextType {
  allEvents: UnifiedEvent[];
  teamMemberships: TeamMember[];
  currentUnifiedEvent: UnifiedEvent | null;
  loading: boolean;
  switchToEvent: (eventId: string, isGeneric: boolean, isManualSelection?: boolean) => Promise<boolean>;
  refreshAllEvents: () => Promise<void>;
  setActiveGenericEvent: (event: UnifiedEvent) => void;
  findClosestEvent: () => UnifiedEvent | null;
}

// Création du contexte
const UnifiedEventContext = createContext<UnifiedEventContextType | undefined>(undefined);

// Props pour le provider
interface UnifiedEventProviderProps {
  children: ReactNode;
}

// Provider component
export const UnifiedEventProvider: React.FC<UnifiedEventProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const {members:teamMemberships,loading:teamLoading}=useTeamMemberships();
  const { events: normalEvents, currentEvent, switchEvent } = useEvent();
  const [genericEvents, setGenericEvents] = useState<any[]>([]);
  const [currentUnifiedEvent, setCurrentUnifiedEvent] = useState<UnifiedEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeGenericEvent, setActiveGenericEventState] = useState<UnifiedEvent | null>(null);
  const [lastUserId, setLastUserId] = useState<string | null>(null);
  const [hasAttemptedAutoSelection, setHasAttemptedAutoSelection] = useState<boolean>(false);

  // Nettoyer les données lors du changement d'utilisateur
  useEffect(() => {
    const userId = currentUser?.uid || null;

    if (lastUserId !== userId) {
      setGenericEvents([]);
      setCurrentUnifiedEvent(null);
      setActiveGenericEventState(null);
      setHasAttemptedAutoSelection(false);
      if (lastUserId) localStorage.removeItem(`activeGenericEvent_${lastUserId}`);
      localStorage.removeItem('activeGenericEvent');
      localStorage.removeItem('accessCodeCollaborator');
      localStorage.removeItem('sharedLinkCollaborator');
      setLastUserId(userId);
    }
  }, [currentUser?.uid, lastUserId]);

  // Charger tous les événements génériques
  const loadGenericEvents = async () => {
    if (!currentUser || currentUser.isAnonymous) return [];
    const allGenericEvents: any[] = [];

    // Liste complète des types d'événements supportés
    const eventTypes = ['wedding', 'birthday', 'engagement', 'babyshower', 'baptism', 'other'];

    for (const eventType of eventTypes) {
      try {
        const events = await genericEventService.getUserEvents(currentUser.uid, eventType);

        allGenericEvents.push(...events.map(event => ({
          ...event,
          isGeneric: true,
          type: eventType
        })));
      } catch (error) {
        console.warn(`Erreur lors du chargement des événements ${eventType}:`, error);
      }
    }

    return allGenericEvents;
  };

  // Combiner les événements automatiquement
  const allEvents = React.useMemo(() => {
    const unified: UnifiedEvent[] = [
      ...teamMemberships.map(member=>member.event as UnifiedEvent),
      // Événements normaux
      ...normalEvents.map(event => ({
        ...event,
        id: event.id,
        name: event.name,
        type: event.type,
        date: event.date || null,
        location: event.location || undefined,
        isGeneric: false,
        targetId: event.weddingId || event.engagementId || event.birthdayId || event.babyShowerId || event.baptismId || event.otherId || event.id
      })),
      // Événements génériques
      ...genericEvents.map(event => ({
        id: event.id || '',
        userId: event.userId,
        name: event.name,
        type: event.type,
        date: event.eventDetails?.date || null,
        location: event.eventDetails?.location?.city || event.eventDetails?.location?.venue || event.eventDetails?.location?.address || undefined,
        isGeneric: true,
        targetId: event.id || '',
        eventDetails: event.eventDetails,
        partners: event.partners || undefined
      }))
    ];

    // Déduplication basée sur l'ID et le type pour éviter les doublons
    return unified.filter((event, index, self) => {
      return index === self.findIndex(e =>
        e.id === event.id &&
        e.isGeneric === event.isGeneric
      );
    });
  }, [normalEvents, genericEvents, teamMemberships]);

  // Rafraîchir tous les événements
  const refreshAllEvents = async () => {
    if (!currentUser || currentUser.isAnonymous) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const genericEvts = await loadGenericEvents();
      setGenericEvents(genericEvts);
      // allEvents will be updated automatically by the useEffect
    } catch (error) {
      console.error('Erreur lors du rafraîchissement des événements:', error);
    } finally {
      setLoading(false);
    }
  };

  // Changer d'événement actuel
  const switchToEvent = async (eventId: string, isGeneric: boolean, isManualSelection = true): Promise<boolean> => {
    try {
      const shared=teamMemberships.find(m=>m.event.id===eventId && m.event.isGeneric===isGeneric);
      if(shared){setActiveGenericEvent(shared.event as UnifiedEvent);return true;}
      if (isGeneric) {
        // Pour un événement générique, on le marque comme actif temporairement
        const event = allEvents.find(e => e.id === eventId && e.isGeneric);
        if (event) {
          setActiveGenericEventState(event);
          setCurrentUnifiedEvent(event);
          // Stocker dans localStorage avec l'ID utilisateur pour éviter les conflits
          if (currentUser?.uid) {
            localStorage.setItem(`activeGenericEvent_${currentUser.uid}`, JSON.stringify(event));
            // Marquer comme sélection manuelle si c'est le cas
            if (isManualSelection) {
              markManualEventSelection(currentUser.uid);
            }
          }
          return true;
        }
      } else {
        // Pour un événement normal, utiliser le système existant
        const success = await switchEvent(eventId);
        if (success) {
          setActiveGenericEventState(null);
          if (currentUser?.uid) {
            localStorage.removeItem(`activeGenericEvent_${currentUser.uid}`);
            // Marquer comme sélection manuelle si c'est le cas
            if (isManualSelection) {
              markManualEventSelection(currentUser.uid);
            }
          }
          const event = allEvents.find(e => e.id === eventId && !e.isGeneric);
          if (event) {
            setCurrentUnifiedEvent(event);
          }
        }
        return success;
      }
      return false;
    } catch (error) {
      console.error('Erreur lors du changement d\'événement:', error);
      return false;
    }
  };

  // Fonction pour trouver l'événement le plus proche en date
  const findClosestEvent = (): UnifiedEvent | null => {
    if (allEvents.length === 0) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset à minuit pour comparer uniquement les dates

    // Séparer les événements avec et sans date
    const eventsWithDate = allEvents.filter(event => event.date);
    const eventsWithoutDate = allEvents.filter(event => !event.date);

    if (eventsWithDate.length === 0) {
      // Si aucun événement n'a de date, retourner le premier événement disponible
      return allEvents[0] || null;
    }

    // Convertir les dates et calculer les différences
    const eventsByDistance = eventsWithDate.map(event => {
      let eventDate: Date;

      if (event.date instanceof Date) {
        eventDate = new Date(event.date);
      } else if (typeof event.date === 'string') {
        eventDate = new Date(event.date);
      } else {
        return null;
      }

      // Si la date est invalide, ignorer cet événement
      if (isNaN(eventDate.getTime())) {
        return null;
      }

      eventDate.setHours(0, 0, 0, 0);

      // Calculer la différence en jours
      const diffTime = eventDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      return {
        event,
        eventDate,
        diffDays,
        isUpcoming: diffDays >= 0,
        isPast: diffDays < 0
      };
    }).filter(item => item !== null);

    if (eventsByDistance.length === 0) {
      return allEvents[0] || null;
    }

    // Trier les événements :
    // 1. Événements à venir (diffDays >= 0) par ordre chronologique
    // 2. Événements passés (diffDays < 0) par ordre chronologique inverse (le plus récent d'abord)
    eventsByDistance.sort((a, b) => {
      if (a!.isUpcoming && b!.isUpcoming) {
        // Deux événements à venir : le plus proche en premier
        return a!.diffDays - b!.diffDays;
      } else if (a!.isPast && b!.isPast) {
        // Deux événements passés : le plus récent en premier
        return b!.diffDays - a!.diffDays;
      } else if (a!.isUpcoming && b!.isPast) {
        // Événement à venir prioritaire sur événement passé
        return -1;
      } else {
        // Événement à venir prioritaire sur événement passé
        return 1;
      }
    });

    eventsByDistance.forEach((item, index) => {
      const status = item!.isUpcoming ? '🔜 À venir' : '✅ Passé';
    });

    return eventsByDistance[0]?.event || allEvents[0] || null;
  };

  // Définir un événement générique comme actif - fonction publique
  const setActiveGenericEvent = (event: UnifiedEvent) => {
    setActiveGenericEventState(event);
    setCurrentUnifiedEvent(event);
    if (currentUser?.uid) {
      localStorage.setItem(`activeGenericEvent_${currentUser.uid}`, JSON.stringify(event));
    }
  };

  useEffect(() => {
    if (!teamLoading && currentUnifiedEvent?.teamKey && !teamMemberships.some(m=>m.teamKey===currentUnifiedEvent.teamKey)) {
      setCurrentUnifiedEvent(null);setActiveGenericEventState(null);
      if(currentUser) localStorage.removeItem(`activeGenericEvent_${currentUser.uid}`);
    }
  }, [teamLoading,teamMemberships,currentUnifiedEvent?.teamKey,currentUser?.uid]);

  // Mettre à jour l'événement unifié actuel
  useEffect(() => {
    if (!currentUser || currentUser.isAnonymous) {
      setCurrentUnifiedEvent(null);
      setActiveGenericEventState(null);
      return;
    }

    // Attendre que les événements soient chargés
    if (loading || teamLoading || allEvents.length === 0) {
      return;
    }

    // Vérifier s'il y a un événement générique actif stocké pour cet utilisateur
    const storedGenericEvent = localStorage.getItem(`activeGenericEvent_${currentUser.uid}`);
    if (storedGenericEvent) {
      try {
        const event = JSON.parse(storedGenericEvent);
        // Vérifier que l'événement appartient bien à cet utilisateur en le cherchant dans allEvents
        const eventExists = allEvents.find(e => e.id === event.id && e.isGeneric === event.isGeneric);
        if (eventExists) {
          setActiveGenericEventState(eventExists);
          setCurrentUnifiedEvent(eventExists);
          return;
        } else {
          // L'événement n'existe plus, nettoyer le localStorage
          localStorage.removeItem(`activeGenericEvent_${currentUser.uid}`);
        }
      } catch (error) {
        localStorage.removeItem(`activeGenericEvent_${currentUser.uid}`);
        console.warn('⚠️ [UnifiedEventContext] Erreur lors de la lecture du localStorage, nettoyé');
      }
    }

    // Si il y a un événement normal actuel, l'utiliser
    if (currentEvent) {
      const unifiedEvent: UnifiedEvent = {
        id: currentEvent.id,
        userId: currentEvent.userId,
        name: currentEvent.name,
        type: currentEvent.type,
        date: currentEvent.date || null,
        location: currentEvent.location || undefined,
        isGeneric: false
      };
      setCurrentUnifiedEvent(unifiedEvent);
      setActiveGenericEventState(null);
      return;
    }

    // Vérifier si on doit sélectionner automatiquement l'événement le plus proche
    // Utiliser hasAttemptedAutoSelection pour éviter les tentatives multiples
    if (!hasAttemptedAutoSelection && shouldAutoSelectClosestEvent(currentUnifiedEvent, allEvents, currentUser.uid)) {
      // Marquer qu'on a tenté la sélection automatique pour éviter les boucles
      setHasAttemptedAutoSelection(true);

      const closestEvent = findClosestEventByDate(allEvents);
      if (closestEvent) {
        // Sélectionner automatiquement l'événement le plus proche (isManualSelection = false)
        switchToEvent(closestEvent.id, closestEvent.isGeneric, false).then((success) => {
          if (success) {} else {
            console.error('❌ [UnifiedEventContext] Échec de la sélection automatique pour:', closestEvent.name);
          }
        }).catch(error => {
          console.error('❌ [UnifiedEventContext] Erreur lors de la sélection automatique:', error);
        });
      } else {}
    } else if (hasAttemptedAutoSelection) {} else {}
  }, [currentEvent, currentUser?.uid, allEvents, loading, teamLoading, hasAttemptedAutoSelection]);

  // Charger les événements au montage
  useEffect(() => {
    if (currentUser) {
      refreshAllEvents();
    } else {
      setGenericEvents([]);
      setCurrentUnifiedEvent(null);
      setActiveGenericEventState(null);
      setLoading(false);
    }
  }, [currentUser, normalEvents]);

  // Nettoyer les sélections manuelles lors de la déconnexion
  useEffect(() => {
    if (!currentUser?.uid && lastUserId) {
      // L'utilisateur vient de se déconnecter
      clearManualEventSelection(lastUserId);
    }
  }, [currentUser?.uid, lastUserId]);

  const value: UnifiedEventContextType = React.useMemo(() => ({
    allEvents,
    teamMemberships,
    currentUnifiedEvent,
    loading: loading || teamLoading,
    switchToEvent,
    refreshAllEvents,
    setActiveGenericEvent,
    findClosestEvent
  }), [allEvents, currentUnifiedEvent, loading, teamLoading, teamMemberships]);

  return (
    <UnifiedEventContext.Provider value={value}>
      {children}
    </UnifiedEventContext.Provider>
  );
};

// Hook pour utiliser le contexte unifié
export const useUnifiedEvent = (): UnifiedEventContextType => {
  const context = useContext(UnifiedEventContext);
  if (context === undefined) {
    throw new Error('useUnifiedEvent must be used within a UnifiedEventProvider');
  }
  return context;
};

export default UnifiedEventContext;